import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { CAL_TYPES, CAL_TYPE_ORDER, type BookableType } from './calendarTypes';

/* Toolbar pieces shared by the Calendar page and the full-timeline modal.
 * They are rendered twice from one piece of state, so filtering inside the
 * modal and filtering on the page behind it are the same act. */

export interface BranchOption {
  id: string;
  name: string;
  displayName: string;
}

export function BranchSelect({ value, onChange, branches }: {
  value: string;
  onChange: (v: string) => void;
  branches: BranchOption[];
}) {
  return (
    <div className="a-selwrap">
      <select
        className="a-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label="Branch"
      >
        <option value="">All branches</option>
        {branches.map(b => (
          <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
        ))}
      </select>
    </div>
  );
}

/** Multi-select type filter. At least one type always stays on — an empty
 *  calendar that looks broken is worse than a filter that refuses to clear. */
export function TypeFilter({ types, onChange, counts }: {
  types: BookableType[];
  onChange: (t: BookableType[]) => void;
  counts: Partial<Record<BookableType, number>>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const all = types.length === CAL_TYPE_ORDER.length;
  const label = all ? 'All types' : types.length === 1 ? CAL_TYPES[types[0]].label : `${types.length} types`;

  const toggle = (t: BookableType) => {
    const next = types.includes(t) ? types.filter(x => x !== t) : [...types, t];
    if (next.length) onChange(next);
  };

  return (
    <div className="a-tf" ref={ref}>
      <button className="a-pill" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-haspopup="true">
        {label}
        <span className="a-tdots">
          {types.map(t => <i key={t} style={{ background: CAL_TYPES[t].c }} />)}
        </span>
      </button>
      {open && (
        <div className="a-tfpop">
          {CAL_TYPE_ORDER.map(t => (
            <label key={t}>
              <input type="checkbox" checked={types.includes(t)} onChange={() => toggle(t)} />
              <span className="a-sw" style={{ background: CAL_TYPES[t].c }} />
              {CAL_TYPES[t].label}
              <span className="ct">{counts[t] || 0}</span>
            </label>
          ))}
          <div className="a-tffoot">
            <button onClick={() => onChange([...CAL_TYPE_ORDER])}>Select all</button>
            <button onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function WeekNav({ label, onPrev, onNext }: {
  label: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="a-wnav">
      <button className="a-ic" onClick={onPrev} aria-label="Previous week"><ChevronLeft className="w-4 h-4" /></button>
      <span>{label}</span>
      <button className="a-ic" onClick={onNext} aria-label="Next week"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options, small, ariaLabel }: {
  value: T;
  onChange: (v: T) => void;
  options: Array<[T, string]>;
  small?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div className={`a-seg${small ? ' sm' : ''}`} role="tablist" aria-label={ariaLabel}>
      {options.map(([k, l]) => (
        <button
          key={k}
          role="tab"
          aria-selected={value === k}
          className={value === k ? 'on' : ''}
          onClick={() => onChange(k)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/** The one loading affordance every calendar surface uses while a fetch is in
 *  flight — including background refreshes that keep the old data on screen. */
export function BusyPill({ label = 'Loading…' }: { label?: string }) {
  return (
    <span className="a-busy" role="status" aria-live="polite">
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      {label}
    </span>
  );
}

/** Type chips under the pulse/list. They double as filters and stay in sync
 *  with the dropdown above. */
export function TypeLegend({ types, onToggle, counts }: {
  types: BookableType[];
  onToggle: (t: BookableType) => void;
  counts: Partial<Record<BookableType, number>>;
}) {
  return (
    <div className="a-lchips">
      {CAL_TYPE_ORDER.map(t => {
        const on = types.includes(t);
        return (
          <button
            key={t}
            className={`a-lchip${on ? '' : ' off'}`}
            onClick={() => onToggle(t)}
            aria-pressed={on}
            title={on ? `Hide ${CAL_TYPES[t].label}` : `Show ${CAL_TYPES[t].label}`}
          >
            <i style={{ background: CAL_TYPES[t].c }} />
            {CAL_TYPES[t].label}
            <span>{counts[t] || 0}</span>
          </button>
        );
      })}
    </div>
  );
}
